// external imports
import React from 'react';
import { Box, Typography, TextField, InputAdornment, Avatar } from '@mui/material';
import { Search } from '@mui/icons-material';

// avatar color palette (cycled by index for a stable, distinct colour per manager)
const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0d9488', '#c2410c', '#be185d', '#4338ca'];

// derive up-to-two-letter initials from a full name
const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();

const ClusterManagers = ({ managers = [], selectedEmpId, onSelect }) => {
  // states
  const [searchTerm, setSearchTerm] = React.useState('');

  // filter managers by name / emp id / region
  const filteredManagers = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return managers;
    return managers.filter(
      (manager) =>
        manager.fullName.toLowerCase().includes(query) ||
        manager.empId.toLowerCase().includes(query) ||
        (manager.regionsLabel || '').toLowerCase().includes(query)
    );
  }, [managers, searchTerm]);

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '340px' },
        flexShrink: 0,
        background: '#ffffff',
        borderRadius: '8px',
        p: 2,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0',
        alignSelf: 'flex-start',
      }}
    >
      <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#1e293b', mb: 1.5 }}>
        {`Cluster Managers (${managers.length})`}
      </Typography>

      {/* Search for a cluster manager */}
      <TextField
        fullWidth
        sx={{ mb: 2 }}
        variant="outlined"
        size="small"
        placeholder="Search managers..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#94a3b8', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      {/* Managers list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {filteredManagers.map((manager, index) => {
          const isSelected = manager.empId === selectedEmpId;
          const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

          return (
            <Box
              key={manager.empId}
              onClick={() => onSelect?.(manager.empId)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '10px',
                cursor: 'pointer',
                border: isSelected ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                background: isSelected ? '#eff6ff' : '#ffffff',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: isSelected ? '#93c5fd' : '#cbd5e1',
                  background: isSelected ? '#eff6ff' : '#f8fafc',
                },
              }}
            >
              {/* Avatar with initials */}
              <Avatar
                sx={{
                  bgcolor: avatarColor,
                  width: 44,
                  height: 44,
                  fontSize: 15,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getInitials(manager.fullName)}
              </Avatar>

              {/* Name + emp id / regions */}
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography
                  sx={{ fontSize: 15, fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}
                >
                  {manager.fullName}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.35 }}>
                  {`${manager.empId} · ${manager.regionsLabel}`}
                </Typography>
              </Box>

              {/* Trainer count badge */}
              <Box
                sx={{
                  flexShrink: 0,
                  minWidth: 26,
                  height: 26,
                  px: 0.75,
                  borderRadius: '999px',
                  background: isSelected ? '#2563eb' : '#f1f5f9',
                  color: isSelected ? '#ffffff' : '#475569',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {manager.trainerCount}
              </Box>
            </Box>
          );
        })}

        {/* Empty state */}
        {filteredManagers.length === 0 && (
          <Typography sx={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', py: 3 }}>
            No managers found.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ClusterManagers;
